import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Avatar
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { getBrands,  createBrand, updateBrand, deleteBrand } from '../../services/brandService';

const BrandManagement = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('create'); // 'create', 'edit', 'delete'
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    prefix: '',
    logo: '',
    primaryColor: '#250c4b',
  });
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const data = await getBrands();
      setBrands(data);
    } catch (err) {
      setError('Markalar yüklenirken bir hata oluştu.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (type, brand = null) => {
    if (brand) {
      setSelectedBrand(brand);
      setFormData({
        id: brand._id,
        name: brand.name,
        prefix: brand.prefix || '',
        logo: brand.logo || '',
        primaryColor: brand.primaryColor || '#250c4b',
      });
    } else {
      setFormData({
        id: '',
        name: '',
        prefix: '',
        logo: '',
        primaryColor: '#250c4b',
      });
    }
    setDialogType(type);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedBrand(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (dialogType === 'create') {
        await createBrand({
          name: formData.name,
          prefix: formData.prefix,
          logo: formData.logo,
          primaryColor: formData.primaryColor,
        });
        setSnackbar({
          open: true,
          message: 'Marka başarıyla oluşturuldu.',
          severity: 'success'
        });
      } else if (dialogType === 'edit') {
        await updateBrand(formData.id, {
          name: formData.name,
          prefix: formData.prefix,
          logo: formData.logo,
          primaryColor: formData.primaryColor,
        });
        setSnackbar({
          open: true,
          message: 'Marka başarıyla güncellendi.',
          severity: 'success'
        });
      } else if (dialogType === 'delete') {
        await deleteBrand(formData.id);
        setSnackbar({
          open: true,
          message: 'Marka başarıyla silindi.',
          severity: 'success'
        });
      }

      // Güncel markaları yeniden yükle
      await fetchBrands();
      handleCloseDialog();
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Bir hata oluştu: ' + (err.response?.data?.message || err.message),
        severity: 'error'
      });
      console.error(err);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h1">Marka Yönetimi</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('create')}
        >
          Yeni Marka
        </Button>
      </Box>

      <Box mb={4}>
        <Typography variant="h6" mb={2}>Markalar</Typography>
        <Box display="flex" flexWrap="wrap" gap={2}>
          {brands.map(brand => (
            <Card key={brand._id} sx={{ width: 200, height: 200 }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
                {brand.logo ? (
                  <Avatar 
                    src={brand.logo} 
                    alt={brand.name}
                    sx={{ width: 80, height: 80, mb: 2 }}
                  />
                ) : (
                  <Avatar 
                    sx={{ width: 80, height: 80, mb: 2, bgcolor: 'primary.main' }}
                  >
                    {brand.name.substring(0, 1).toUpperCase()}
                  </Avatar>
                )}
                <Typography variant="h6" align="center" gutterBottom>{brand.name}</Typography>
                {brand.prefix && (
                  <Typography variant="body2" align="center" color="textSecondary" gutterBottom>
                    Prefix: {brand.prefix}
                  </Typography>
                )}
                <Box mt="auto" display="flex" justifyContent="center" width="100%">
                  <IconButton color="info" onClick={() => handleOpenDialog('edit', brand)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton color="error" onClick={() => handleOpenDialog('delete', brand)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Marka Adı</TableCell>
              <TableCell>Kısaltma</TableCell>
              <TableCell>Accent</TableCell>
              <TableCell>Logo</TableCell>
              <TableCell>Oluşturulma</TableCell>
              <TableCell align="right">İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {brands.map(brand => (
              <TableRow key={brand._id}>
                <TableCell>{brand.name}</TableCell>
                <TableCell>{brand.prefix}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: 0.5,
                        bgcolor: brand.primaryColor || '#250c4b',
                        border: '1px solid #ddd',
                      }}
                    />
                    <Typography variant="caption">{brand.primaryColor || '—'}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {brand.logo ? (
                    <Avatar 
                      src={brand.logo} 
                      alt={brand.name}
                      sx={{ width: 40, height: 40 }}
                    />
                  ) : (
                    <Avatar 
                      sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}
                    >
                      {brand.name.substring(0, 1).toUpperCase()}
                    </Avatar>
                  )}
                </TableCell>
                <TableCell>
                  {new Date(brand.createdAt).toLocaleDateString('tr-TR')}
                </TableCell>
                <TableCell align="right">
                  <IconButton color="info" onClick={() => handleOpenDialog('edit', brand)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton color="error" onClick={() => handleOpenDialog('delete', brand)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog && (dialogType === 'create' || dialogType === 'edit')} onClose={handleCloseDialog}>
        <DialogTitle>{dialogType === 'create' ? 'Yeni Marka Ekle' : 'Markayı Düzenle'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Marka Adı"
            type="text"
            fullWidth
            value={formData.name}
            onChange={handleChange}
            required
          />
          <TextField
            margin="dense"
            name="prefix"
            label="Marka Kısaltması (Örn: IP, KA, PT)"
            type="text"
            fullWidth
            value={formData.prefix}
            onChange={handleChange}
            required
            inputProps={{ maxLength: 5 }}
            helperText="Maximum 5 karakter, rapor isimlerinin başında kullanılacak (Örn: IP-123)"
          />
          <TextField
            margin="dense"
            name="logo"
            label="Logo URL"
            type="text"
            fullWidth
            value={formData.logo}
            onChange={handleChange}
            helperText="Logo URL'ini buraya girebilirsiniz (opsiyonel)"
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, mb: 1 }}>
            <TextField
              margin="dense"
              name="primaryColor"
              label="Accent rengi"
              type="color"
              sx={{ width: 120 }}
              value={formData.primaryColor}
              onChange={handleChange}
            />
            <TextField
              margin="dense"
              name="primaryColor"
              label="Hex kodu"
              type="text"
              value={formData.primaryColor}
              onChange={handleChange}
              inputProps={{ maxLength: 7 }}
              helperText="Marka sayfası vurgu rengi (#RRGGBB)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>İptal</Button>
          <Button onClick={handleSubmit} color="primary">
            {dialogType === 'create' ? 'Ekle' : 'Güncelle'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={openDialog && dialogType === 'delete'} onClose={handleCloseDialog}>
        <DialogTitle>Markayı Sil</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <b>{selectedBrand?.name}</b> markasını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>İptal</Button>
          <Button onClick={handleSubmit} color="error">
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BrandManagement; 